import { InteractionType } from "@/common/types";
import {
	AttachmentMetadata,
	AttachmentMetadataInProgress,
	ChatMessage,
	CustomMessage,
} from "@azure/communication-react";

export interface CommonChatMessage {
	id: string;
	senderName: string;
	senderId?: string;
	body: string;
	timestamp: string;
	creatorId: string;
}

export interface WebchatAttachment {
	id?: string;
	name: string;
	url: string;
	extension?: string;
}

export enum WebchatMessageType {
	Unknown = -1,
	Text = 0,
	ParticipantJoined = 1,
	ParticipantLeft = 2,
	Announcement = 3,
}

export interface WebchatMessage extends CommonChatMessage {
	attachments?: WebchatAttachment[];
	type?: WebchatMessageType;
}

export interface TranscriptionMessage extends CommonChatMessage {
}

export interface WhatsAppMessage extends CommonChatMessage {
	status: WhatsAppMessageStatus;
	type: WhatsAppMessageType;
	isFromCustomer: boolean;
	filename?: string;
	mediaId?: string;
	mimeType?: string;
	caption?: string;
	mediaUrl?: string;
}

export interface CreateWhatsAppMessage {
	id: string;
	input: string;
	status: WhatsAppMessageStatus;
	type: WhatsAppMessageType;
}

export interface UploadWhatsAppMedia {
	caption?: string;
	filename: string;
	file: File;
	type: WhatsAppMessageType;
}

export interface CCAttachmentMetadataInProgress
	extends AttachmentMetadataInProgress {
	file: File;
	uploadUrl?: string;
}

export interface WhatsAppCCAttachmentMetadataInProgress
	extends CCAttachmentMetadataInProgress {
	type: WhatsAppMessageType;
}

export enum WhatsAppMessageStatus {
	Sending,
	Delivered,
	Read,
	Failed,
	ReengagementFailed,
	MediaUploadFailed,
}

export interface ExtendedChatMessage extends Omit<ChatMessage, "attachments"> {
	showStatus: boolean;
	statusElement?: JSX.Element;
	attachments?: WebchatAttachmentMetadata[];
}

export interface ExtendedCustomMessage extends CustomMessage {
	showStatus: boolean;
	statusElement?: JSX.Element;
}

export interface ExtendedSystemMessage {
	messageType: "system";
	systemMessageType: "content";
	messageId: string;
	createdOn: Date;
	content?: string;
	iconName?: string;
	isChatClosedMessage?: boolean;
	isAnnouncement?: boolean;
}

export interface WebchatAttachmentMetadata extends AttachmentMetadata {
	extension?: string;
}

export enum WhatsAppMessageType {
	Unknown = -1,
	Text = 0,
	Image,
	Video,
	Document,
	Audio,
	Sticker,
	Reaction,
	Location,
	Contacts,
}

export const MimeToWhatsAppTypeMap: Record<string, WhatsAppMessageType> = {
	"audio/vnd.dlna.adts": WhatsAppMessageType.Audio,
	"audio/aac": WhatsAppMessageType.Audio,
	"audio/amr": WhatsAppMessageType.Audio,
	"audio/mpeg": WhatsAppMessageType.Audio,
	"audio/mp4": WhatsAppMessageType.Audio,
	"audio/m4a": WhatsAppMessageType.Audio,
	"audio/x-m4a": WhatsAppMessageType.Audio,
	"audio/ogg": WhatsAppMessageType.Audio,

	"image/jpeg": WhatsAppMessageType.Image,
	"image/png": WhatsAppMessageType.Image,
	"image/webp": WhatsAppMessageType.Image,

	"video/3gpp": WhatsAppMessageType.Video,
	"video/mp4": WhatsAppMessageType.Video,

	"text/plain": WhatsAppMessageType.Document,
	"application/pdf": WhatsAppMessageType.Document,
	"application/msword": WhatsAppMessageType.Document,
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document":
		WhatsAppMessageType.Document,
	"application/vnd.ms-excel": WhatsAppMessageType.Document,
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
		WhatsAppMessageType.Document,
	"application/vnd.ms-powerpoint": WhatsAppMessageType.Document,
	"application/vnd.openxmlformats-officedocument.presentationml.presentation":
		WhatsAppMessageType.Document,
	"application/vnd.oasis.opendocument.spreadsheet":
		WhatsAppMessageType.Document,
};

export const MediaTypeLimits: Record<string, number> = {
	"image/jpeg": 5 * 1024 * 1024, // 5 MB
	"image/png": 5 * 1024 * 1024, // 5 MB
	"image/webp": 100 * 1024, // 100 KB for static stickers

	"text/plain": 25 * 1024 * 1024, // 25 MB
	"application/pdf": 25 * 1024 * 1024, // 25 MB
	"application/msword": 25 * 1024 * 1024, // 25 MB
	"application/vnd.ms-powerpoint": 25 * 1024 * 1024, // 25 MB
	"application/vnd.ms-excel": 25 * 1024 * 1024, // 25 MB
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document":
		25 * 1024 * 1024, // 25 MB
	"application/vnd.openxmlformats-officedocument.presentationml.presentation":
		25 * 1024 * 1024, // 25 MB
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
		25 * 1024 * 1024, // 25 MB

	"audio/vnd.dlna.adts": 16 * 1024 * 1024, // 16 MB
	"audio/aac": 16 * 1024 * 1024, // 16 MB
	"audio/amr": 16 * 1024 * 1024, // 16 MB
	"audio/mpeg": 16 * 1024 * 1024, // 16 MB
	"audio/m4a": 16 * 1024 * 1024, // 16 MB
	"audio/x-m4a": 16 * 1024 * 1024, // 16 MB
	"audio/ogg": 16 * 1024 * 1024, // 16 MB

	"video/mp4": 16 * 1024 * 1024, // 16 MB
	"video/3gpp": 16 * 1024 * 1024, // 16 MB
};

export const imageExtensions = ["jpeg", "jpg", "png", "webp"];

export const imageMimeTypes = ["image/jpeg", "image/png", "image/webp"];

export const messagingAllowedExtensions = [
	".aac",
	".amr",
	".mp3",
	".m4a",
	".ogg",
	".jpeg",
	".jpg",
	".png",
	".webp",
	".3gp",
	".mp4",
	".txt",
	".pdf",
	".doc",
	".docx",
	".xls",
	".xlsx",
	".ppt",
	".pptx",
	".ods",
];

export interface MessagingInteractionContentProps {
	hideSendBox?: boolean;
	isHistory?: boolean;
	interactionId: string;
	type: InteractionType;
	isVariableTextOpen?: boolean;
	date?: string;
}

export interface MessagingMethods {
	chatMessages: any[];
	apiMessages: any[];
	sendMessages: (content: string, attachments?: any[]) => Promise<void>;
	onRenderMessage?: (message: any) => JSX.Element;
	onRenderAttachments?: (message: any) => JSX.Element;
	isChatClosed?: boolean;
}

export enum DisplayMessageType {
	Chat = "chat",
	System = "system",
	Custom = "custom",
}